import { useState } from "react";
import { useTranslations } from "next-intl";
import { apiErrorText, readApiErrorResponse } from "@/lib/api-error";
import { platformFetch } from "@/services/http/platform-fetch";

/** MT-1 Plan 2 S14 (FR-36 UI): rename a school (the address never changes). */
export function SchoolRenameForm({
  tenantId,
  currentName,
  onRenamed,
}: {
  tenantId: string;
  currentName: string;
  onRenamed?: (name: string) => void;
}) {
  const t = useTranslations("platform.school.rename");
  const tRoot = useTranslations();
  const [name, setName] = useState(currentName);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setSaving(true);
    setMessage(null);
    setError(null);
    try {
      const res = await platformFetch(`/platform/admin/tenants/${tenantId}`, {
        method: "PATCH",
        body: JSON.stringify({ name }),
      });
      if (!res.ok) throw await readApiErrorResponse(res);
      const json = (await res.json()) as { data: { tenant: { name: string } } };
      setMessage(t("saved"));
      onRenamed?.(json.data.tenant.name);
    } catch (err) {
      setError(
        apiErrorText(err, tRoot, "platform.school.rename.failed", { namespace: "platform.errors" }),
      );
    } finally {
      setSaving(false);
    }
  }

  return (
    <form onSubmit={submit} className="mt-6 space-y-2" data-testid="school-rename-form">
      <h2 className="text-lg font-semibold">{t("title")}</h2>
      <label htmlFor="school-rename-name" className="block text-sm">
        {t("label")}
      </label>
      <input
        id="school-rename-name"
        required
        maxLength={120}
        value={name}
        aria-describedby="school-rename-hint"
        onChange={(e) => setName(e.target.value)}
        className="w-full rounded-md border border-[var(--border-strong)] bg-[var(--surface-1)] px-3 py-2 text-[var(--text-primary)]"
      />
      <p id="school-rename-hint" className="text-xs text-[var(--text-muted)]">
        {t("hint")}
      </p>
      <button
        type="submit"
        disabled={saving}
        className="rounded-md border border-[var(--border-strong)] px-3 py-2 text-sm"
      >
        {saving ? t("saving") : t("save")}
      </button>
      <div aria-live="polite">{message && <p className="text-sm">{message}</p>}</div>
      {error && (
        <p role="alert" className="text-sm text-[var(--danger)]">
          {error}
        </p>
      )}
    </form>
  );
}
